import { useEffect, useState } from "react";
import { MessageCircle, Check, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

export default function SellerWhatsAppPlans() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/whatsapp/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
    api.get("/seller/whatsapp/subscription").then((r) => { setSub(r.data.subscription); setPurchases(r.data.purchases || []); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const buy = async (p) => {
    setBusy(p.plan_id);
    try { const r = await api.post("/whatsapp/plans/buy", { plan_id: p.plan_id }); toast.success(r.data.message || "Request submitted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="seller-whatsapp-plans">
      <PageHeader title="Bulk WhatsApp" icon={MessageCircle} subtitle="Reach your customers at scale. Choose a bulk WhatsApp messaging plan for your store." />

      {sub && (
        <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="active-wa-plan">
          <p className="text-sm font-semibold text-emerald-800">Active plan: {sub.plan_name} · {sub.messages?.toLocaleString()} messages</p>
          <p className="text-xs text-emerald-600">Valid until {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3" data-testid="wa-plans-grid">
        {plans.map((p) => (
          <div key={p.plan_id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5" data-testid={`wa-plan-${p.plan_id}`}>
            <p className="text-sm font-semibold text-slate-500">{p.name}</p>
            <p className="mt-1 text-3xl font-extrabold text-slate-900">₹{p.price}<span className="text-sm font-normal text-slate-400">/{p.validity_days}d</span></p>
            <p className="mt-2 text-sm text-slate-600">{p.description}</p>
            <ul className="mt-3 flex-1 space-y-1 text-sm text-slate-600">
              <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" /> {p.messages?.toLocaleString()} messages</li>
              <li className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" /> {p.validity_days}-day validity</li>
            </ul>
            <button onClick={() => buy(p)} disabled={busy === p.plan_id} data-testid={`buy-${p.plan_id}`} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
              {busy === p.plan_id ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Buy plan
            </button>
          </div>
        ))}
      </div>

      {purchases.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-bold text-slate-700">Your requests</h3>
          <div className="space-y-2">
            {purchases.map((p) => (
              <div key={p.purchase_id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm" data-testid={`purchase-${p.purchase_id}`}>
                <span>{p.plan_name} · ₹{p.price}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.status === "activated" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="mt-6 text-xs text-slate-400">Payment is confirmed by the Suffle team, who then activate your plan. You'll see it as Active above once enabled.</p>
    </div>
  );
}
